import TotalMetric from '@/Enums/ReportingDashboard/TotalMetric.enum';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { CustomIcon } from '@/types/Icons/Icon.type';
import { Box, CircularProgress, Paper, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { ReactNode, useMemo } from 'react';
import { METRIC_OPTIONS } from './constants';

interface IProps {
  value: number | null;
  metric: TotalMetric;
  label?: ReactNode;
  Icon?: CustomIcon;
  valueConverter?: (value: number | null, maximumFractionDigits?: number, minimumFractionDigits?: number) => string;
  className?: string;
  color?: string;
  loading?: boolean;
  // When `value === null`, the slide renders an em-dash placeholder. If
  // `nullTooltip` is set, the placeholder is wrapped in a tooltip explaining
  // why the value is missing (e.g. "still computing").
  nullTooltip?: string;
}

const TotalSlide = ({ value, metric, ...props }: IProps) => {
  const { Icon, label, color, loading, className, valueConverter, nullTooltip } = useMemo(
    () => ({
      valueConverter: numberToUsFormat,
      ...METRIC_OPTIONS[metric],
      ...props,
    }),
    [metric, props],
  );

  // Render an em-dash for null values; the API uses null to signal that the
  // metric is still computing (or otherwise unresolved). Wrap in a tooltip
  // when one is provided so users can distinguish "no data" from "loading".
  const isNullValue = value === null;
  const displayValue = isNullValue ? '—' : valueConverter(value);
  const valueNode = (
    <span className='text-3xl font-bold ml-[20px] mb-[6px]' style={isNullValue ? { cursor: 'help' } : undefined}>
      {displayValue}
    </span>
  );

  return (
    <Paper className={classNames('flex flex-1 print:p-[10px] flex-col p-[22px] pb-[28px]', className)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          marginBottom: '12px',
          fontWeight: 600,
          color,
        }}
        className='text-sm'
      >
        <Icon className='w-[16px]' />
        {label}
      </Box>

      {loading ? (
        <div className='flex w-full justify-center'>
          <CircularProgress />
        </div>
      ) : isNullValue && nullTooltip ? (
        <Tooltip title={nullTooltip} arrow>
          {valueNode}
        </Tooltip>
      ) : (
        valueNode
      )}
    </Paper>
  );
};

export default TotalSlide;
